/**
 * Shared utilities for building Insights chat request payloads.
 * Used by both the Insights page and the ChatPanel component.
 */

/**
 * Serialize chat history for the /insights/chat request body.
 * @param {Array} chatHistory - Redux chat history (messages with role, content, etc.)
 * @return {Array} History entries with role, content, parameters, html, index
 */
export function serializeChatHistoryForRequest(chatHistory) {
  if (!Array.isArray(chatHistory)) return [];
  return chatHistory.map((msg, index) => {
    return {
      role: msg.role,
      content: msg.content,
      parameters: msg.parameters,
      html: msg.html || undefined,
      index,
    };
  });
}

/**
 * Build the request payload for POST /insights/chat.
 * @param {Object} options
 * @param {string} options.message - User message text
 * @param {Array} options.chatHistory - Redux chat history
 * @param {Object} [options.constantOverrides] - Optional overrides to include when present
 * @return {Object} Payload for JSON.stringify
 */
export function buildChatRequestPayload({
  message,
  chatHistory,
  constantOverrides,
  sessionId,
  threadId,
  clarificationPayload,
  abortClarification,
}) {
  const payload = {
    message,
    timeZone: new Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    sessionId,
    threadId,
    history: serializeChatHistoryForRequest(chatHistory),
  };
  if (constantOverrides && Object.keys(constantOverrides).length > 0) {
    payload.constantOverrides = constantOverrides;
  }
  if (Array.isArray(clarificationPayload) && clarificationPayload.length > 0) {
    payload.clarificationPayload = clarificationPayload;
  }
  if (abortClarification) {
    payload.abortClarification = true;
  }
  return payload;
}

/**
 * Check if the last assistant message is a clarification that shows a picker.
 * @param {Array} chatHistory - Redux chat history
 * @return {boolean}
 */
export function isInClarificationPickerMode(chatHistory) {
  if (!Array.isArray(chatHistory)) return false;
  for (let i = chatHistory.length - 1; i >= 0; i--) {
    if (chatHistory[i].role === 'assistant') {
      const ct = chatHistory[i].clarificationType;
      return chatHistory[i].mode === 'clarification' && (ct === 'groupDefinition' || ct === 'typedDefinitionReview');
    }
  }
  return false;
}

/**
 * Check if the text input should be in "group definition mode" (green bar).
 * This requires both: a clarification picker is active AND the user opted into
 * describing in their own words (via the "Describe in your own words" link).
 * @param {Array} chatHistory - Redux chat history
 * @param {boolean} textDefinitionMode - Redux flag set when the user opts into describing in words
 * @return {boolean}
 */
export function isInGroupDefinitionMode(chatHistory, textDefinitionMode) {
  return isInClarificationPickerMode(chatHistory) && textDefinitionMode === true;
}
